import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bot, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton, Spinner } from '@/components/ui/misc';
import { apiFetch } from '@/lib/api';
import { useI18n } from '@/lib/i18n';

interface ConsentScope {
  id: string;
  default: boolean;
  allowed: boolean;
  requested: boolean;
}

interface ConsentRequest {
  client_name: string;
  redirect_host: string;
  role: string;
  active_project: string;
  projects: { id: string; name: string; api_mode: string }[];
  scopes: ConsentScope[];
}

// bzapper:read → aiConnect.scope.read (as chaves do i18n não levam ":").
const scopeKey = (id: string) => `aiConnect.scope.${id.replace('bzapper:', '')}`;

// ConectarIA — o consentimento do conector de IA (MCP). O aplicativo de IA (Claude,
// ChatGPT, Cursor…) manda a pessoa para cá pelo OAuth; ela escolhe os projetos e o que
// a IA pode fazer, e volta para o aplicativo com o código. A leitura vem marcada; o que
// envia, gasta ou altera é escolha explícita (docs/plano-mcp.md §4).
export function ConectarIA() {
  const { t } = useI18n();
  const req = useMemo(() => new URLSearchParams(window.location.search).get('req') ?? '', []);
  const [projects, setProjects] = useState<string[]>([]);
  const [scopes, setScopes] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['mcp-consent', req],
    enabled: req !== '',
    retry: false,
    queryFn: async (): Promise<ConsentRequest> => {
      const r = await apiFetch('GET', `/mcp-oauth/request?req=${encodeURIComponent(req)}`);
      if (!r.ok) throw new Error((r.data as { message?: string })?.message ?? t('aiConnect.invalid'));
      return r.data as ConsentRequest;
    },
  });

  // Valores iniciais: o projeto ativo no painel (ou o primeiro) e as permissões que o
  // aplicativo pediu E que vêm marcadas por padrão — hoje, só a leitura.
  useEffect(() => {
    if (!data) return;
    const active = data.projects.find((p) => p.id === data.active_project) ?? data.projects[0];
    setProjects(active ? [active.id] : []);
    setScopes(data.scopes.filter((s) => s.default && s.allowed).map((s) => s.id));
  }, [data]);

  const toggle = (list: string[], id: string, on: boolean) => (on ? [...list, id] : list.filter((x) => x !== id));

  const answer = async (approve: boolean) => {
    setBusy(true);
    setFailure('');
    try {
      const r = await apiFetch('POST', '/mcp-oauth/consent', { req, approve, project_ids: projects, scopes });
      const url = (r.data as { redirect_url?: string })?.redirect_url;
      if (r.ok && url) {
        window.location.href = url; // volta ao aplicativo de IA com o código (ou a recusa)
        return;
      }
      setFailure((r.data as { message?: string })?.message ?? t('aiConnect.failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-muted/30 px-4 py-10">
      <Card className="w-full max-w-lg">
        <CardContent className="flex flex-col gap-6 p-6">
          {!req || error ? (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <Bot className="size-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">{error ? (error as Error).message : t('aiConnect.invalid')}</p>
            </div>
          ) : isLoading || !data ? (
            <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : (
            <>
              <div className="flex items-start gap-3">
                <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary"><Bot className="size-6" /></span>
                <div>
                  <h1 className="text-lg font-semibold">{t('aiConnect.title', { name: data.client_name })}</h1>
                  <p className="text-sm text-muted-foreground">{t('aiConnect.returnTo', { host: data.redirect_host })}</p>
                </div>
              </div>

              <section className="flex flex-col gap-2">
                <h2 className="text-sm font-medium">{t('aiConnect.projects')}</h2>
                {data.projects.map((p) => (
                  <label key={p.id} className="flex cursor-pointer items-center gap-2 text-sm">
                    <input type="checkbox" className="size-4 accent-primary" checked={projects.includes(p.id)}
                      onChange={(e) => setProjects(toggle(projects, p.id, e.target.checked))} />
                    <span className="flex-1">{p.name}</span>
                    <Badge variant="outline">{p.api_mode === 'OFFICIAL' ? t('aiConnect.railOfficial') : t('aiConnect.railQR')}</Badge>
                  </label>
                ))}
              </section>

              <section className="flex flex-col gap-2">
                <h2 className="text-sm font-medium">{t('aiConnect.permissions')}</h2>
                {data.scopes.map((s) => {
                  const fixed = s.id === 'bzapper:read';
                  return (
                    <label key={s.id} className={`flex items-start gap-2 text-sm ${s.allowed ? 'cursor-pointer' : 'opacity-60'}`}>
                      <input type="checkbox" className="mt-0.5 size-4 accent-primary" disabled={fixed || !s.allowed}
                        checked={fixed || scopes.includes(s.id)}
                        onChange={(e) => setScopes(toggle(scopes, s.id, e.target.checked))} />
                      <span className="flex-1">
                        <span className="font-medium">{t(`${scopeKey(s.id)}.title`)}</span>
                        <span className="block text-xs text-muted-foreground">
                          {s.allowed ? t(`${scopeKey(s.id)}.desc`) : t('aiConnect.adminOnly')}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </section>

              <p className="flex items-start gap-2 text-xs text-muted-foreground">
                <ShieldCheck className="mt-0.5 size-4 shrink-0" /> {t('aiConnect.rules')}
              </p>

              {failure && <p className="text-sm text-destructive">{failure}</p>}

              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => answer(false)} disabled={busy}>{t('aiConnect.deny')}</Button>
                <Button onClick={() => answer(true)} disabled={busy || projects.length === 0}>
                  {busy && <Spinner className="border-primary-foreground/40 border-t-primary-foreground" />} {t('aiConnect.authorize')}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
